/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  AudioPresets,
  DefaultReconnectPolicy,
  type RoomOptions,
  ScreenSharePresets,
  type TrackPublishDefaults,
  type VideoPreset,
  VideoPresets,
} from "livekit-client";

const defaultLiveKitPublishOptions: TrackPublishDefaults = {
  // Vault: speech (24 kbps) rather than upstream's music (48 kbps). Opus picks its audio
  // bandwidth from the bitrate: at 48 kbps it stays fullband and reproduces the room as
  // faithfully as the voice, which on a phone held to the ear reads as harsh highs and
  // amplified background noise. Speech keeps it in the voice band, where intelligibility
  // lives, and costs less on a cellular link. This is a call, not a concert.
  audioPreset: AudioPresets.speech,
  // Vault: disable Opus DTX. With DTX the published Opus track carries `usedtx=1`, and the SFU's
  // subscriber offer then ends up with two Opus descriptors sharing payload type 111 (one with
  // `usedtx`, one without). Recent Chromium/WebView (M149+) rejects this as a "BUNDLE group codec
  // collision" (INVALID_PARAMETER), so the subscriber PeerConnection never connects and remote audio
  // is never received → silent calls. Disabling DTX removes the conflicting descriptor.
  dtx: false,
  // Upstream disables RED because the livekit server strips out red packets for clients
  // that don't support it (firefox) but of course that doesn't work with e2ee.
  // Vault: enable RED (packet redundancy) on mobile only, where the runtime is always
  // Chromium WebView / WKWebView and cellular links routinely drop packets. Redundancy is
  // what keeps audio intelligible on weak radio links; bitrate/quality is unaffected on
  // good networks. Desktop web keeps RED off so a Firefox participant can still hear
  // everyone. Known residual gap: a Firefox subscriber in a call with a mobile publisher
  // still can't decode that RED track (e2ee prevents the server-side fallback).
  red: /Android|iPhone|iPad/i.test(navigator.userAgent),
  forceStereo: false,
  simulcast: true,
  videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] as VideoPreset[],
  screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
  stopMicTrackOnMute: false,
  videoCodec: "vp8",
  videoEncoding: VideoPresets.h720.encoding,
  backupCodec: { codec: "vp8", encoding: VideoPresets.h720.encoding },
} as const;

export const defaultLiveKitOptions: RoomOptions = {
  // automatically manage subscribed video quality
  adaptiveStream: true,

  // optimize publishing bandwidth and CPU for published tracks
  dynacast: true,

  // capture settings
  videoCaptureDefaults: {
    resolution: VideoPresets.h720.resolution,
  },

  // publish settings
  publishDefaults: defaultLiveKitPublishOptions,

  // default LiveKit options that seem to be sane
  stopLocalTrackOnUnpublish: true,
  reconnectPolicy: new DefaultReconnectPolicy(),
  disconnectOnPageLeave: true,
  webAudioMix: false,
};
